import { Component, HostListener, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { CustomRouter } from 'src/admin-front/ajs-upgraded-providers';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { SearchBarService } from 'src/app/shared/components/search-bar/search-bar.service';
import { TableColumnTypeEnum } from 'src/app/shared/components/table/shared/enums';
import { TableConfig, TableFilters } from 'src/app/shared/components/table/shared/models';
import { TableLabelCellEnum } from 'src/app/shared/components/table/shared/models/table-label-cell.enum';
import { BasicService } from 'src/app/shared/services/basic.service';
import { AlcmeonAiService } from '../alcmeon-ai.service';
import { KnowledgeDocument } from '../shared/models';

@Component({
  selector: 'app-knowledge-documents',
  templateUrl: './knowledge-documents.component.html',
  styleUrls: ['./knowledge-documents.component.scss'],
})
export class KnowledgeDocumentsComponent implements OnInit, OnDestroy {
  isGaiaExists = false;

  config: TableConfig = {
    displayedColumns: [
      'name',
      'isActive',
      'sourceTypeString',
      'status',
      'lastUpdated',
      'usedByAiAgentsList',
      'actions',
    ],
    columns: [
      {
        name: 'name',
        translationKey: 'parameters/alcmeon-ai/docs/table/col/name',
        property: 'nameLink',
        sortEnabled: true,
        type: TableColumnTypeEnum.LINK,
      },
      {
        name: 'isActive',
        translationKey: 'parameters/alcmeon-ai/docs/table/col/activation',
        property: 'isActive',
        sortEnabled: true,
        type: TableColumnTypeEnum.ACTIVE,
      },

      {
        name: 'sourceTypeString',
        translationKey: 'parameters/alcmeon-ai/docs/table/col/type',
        property: 'sourceTypeString',
        sortEnabled: true,
      },

      {
        name: 'status',
        translationKey: 'parameters/alcmeon-ai/docs/table/col/status',
        property: 'statusString',
        sortEnabled: true,
        type: TableColumnTypeEnum.LABEL,
      },

      {
        name: 'lastUpdated',
        translationKey: 'parameters/alcmeon-ai/docs/table/col/synchro-date',
        property: 'lastUpdated',
        sortEnabled: true,
        type: TableColumnTypeEnum.DATE,
      },

      {
        name: 'usedByAiAgentsList',
        translationKey: 'parameters/alcmeon-ai/docs/table/col/ai-agent',
        property: 'usedByAiAgentsList',
        type: TableColumnTypeEnum.LINK_LIST,
      },
    ],
    actions: [
      {
        tooltip: 'delete',
        clickFn: (doc: KnowledgeDocument) => this.deleteDocument(doc.uuid),
        icon: 'delete',
        color: 'warn',
      },
    ],
  };

  filters: TableFilters = {};

  private subscriptions = new Subscription();

  @HostListener('window:resize')
  onWindowResizeUpdateActionsPosition() {
    this.searchBarService.checkScrollPosition();
  }

  constructor(
    private alcmeonAiService: AlcmeonAiService,
    private alcmeonContextService: AlcmeonContextService,
    private basicService: BasicService,
    private customRouter: CustomRouter,
    private dialog: MatDialog,
    private searchBarService: SearchBarService,
    @Inject('TopToolbarFactory') private topToolbarService: any
  ) {}

  ngOnInit(): void {
    this.topToolbarService.setTitle(
      `${this.basicService.translate('parameters/alcmeon-ai/title')} › ${this.basicService.translate(
        'parameters/alcmeon-ai/tab/docs'
      )}`,
      null
    );

    this.alcmeonAiService.isGaiaConfigExists().subscribe(isGaiaExists => {
      this.isGaiaExists = isGaiaExists;
      if (isGaiaExists) {
        this.searchBarService.refererAsync$ = this.alcmeonAiService.getKnowledgeDocuments;
        this.searchBarService.formatter = this.formatItem;
        this.searchBarService.submitQuery();
      }
    });
  }

  formatItem = (knowledgeDocument: KnowledgeDocument) => {
    const { name, uuid } = knowledgeDocument;
    // URL related to the state auth.alcmeon.fuse.parameters.alcmeon-ai.knowledge-documents.edit
    const knowledgeDocumentHref = this.basicService.fullURLForRoutePath(
      `/#/${this.alcmeonContextService.companyId}/fuse/parameters/alcmeon-ai/knowledge-documents/edit/${uuid}`
    );
    knowledgeDocument.nameLink = {
      text: name,
      href: knowledgeDocumentHref,
      newTab: false,
    };

    knowledgeDocument.sourceTypeString = knowledgeDocument.sourceType
      ? this.basicService.translate(`parameters/alcmeon-ai/docs/type/${knowledgeDocument.sourceType.toLowerCase()}`)
      : '';

    knowledgeDocument.usedByAiAgentsList =
      knowledgeDocument.usedByAiAgents?.map(({ name, id }) => {
        // URL related to the state auth.alcmeon.fuse.parameters.alcmeon-ai.agents.edit
        const aiAgentHref = this.basicService.fullURLForRoutePath(
          `/#/${this.alcmeonContextService.companyId}/fuse/parameters/alcmeon-ai/agents/edit/${id}`
        );
        return {
          text: name,
          href: aiAgentHref,
          newTab: true,
        };
      }) || [];

    let color = null;
    switch (knowledgeDocument.status) {
      case 'FAILED':
        color = TableLabelCellEnum.RED;
        break;
      case 'FINISHED':
        color = TableLabelCellEnum.GREEN;
        break;
      case 'PENDING':
        color = TableLabelCellEnum.GREY;
        break;
      case 'STARTED':
        color = TableLabelCellEnum.YELLOW;
        break;
    }

    knowledgeDocument.statusString = {
      text: this.basicService.translate(`parameters/alcmeon-ai/docs/status/${knowledgeDocument.status.toLowerCase()}`),
      color,
    };
  };

  newDocument = () => {
    this.customRouter.go('auth.alcmeon.fuse.parameters.alcmeon-ai.knowledge-documents.create');
  };

  deleteDocument(uuid: string) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        description: this.basicService.translate('parameters/alcmeon-ai/docs/delete-confirmation'),
        confirmBtnText: this.basicService.translate('ok'),
        cancelBtnText: this.basicService.translate('cancel'),
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.basicService.showSpinner();
        this.alcmeonAiService.deleteKnowledgeDocument(uuid).subscribe(() => {
          this.searchBarService.submitQuery();
          this.basicService.toastSuccess('parameters/alcmeon-ai/docs/delete-success');
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
    this.searchBarService.onDidLeave();
  }
}
